import { parseISO } from "date-fns";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import type {
  CreateEventRequest,
  CreateEventsRequest,
  EventCategoryDto,
  EventDetailsDto,
  UpdateEventRequest,
} from "@/api/types";
import { getDefaultEventDates } from "@/helpers/date-range";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import { eventMutations } from "@/queries/events/mutations";
import { routes } from "@/routes";

import type { CreateOrEditEventFormValues, EventObserver } from "./EventForm";

function getMaxParticipants(noLimit?: boolean, maxParticipants?: string): CreateEventRequest["maxParticipants"] {
  if (noLimit) {
    return undefined;
  }
  if (maxParticipants == null) {
    return undefined;
  }
  if (maxParticipants === "") {
    return undefined;
  }

  return +maxParticipants;
}

export function getDefaultFormValues(
  isClone: boolean,
  eventCategories: EventCategoryDto[],
  event: EventDetailsDto | undefined,
  eventObservers: EventObserver[],
): CreateOrEditEventFormValues {
  const defaultDates = getDefaultEventDates();

  return {
    name: event?.name || "",
    description: event?.description || "",
    location: event?.location || "",
    start: event?.startsAt ? parseISO(event.startsAt) : defaultDates.start,
    end: event?.endsAt ? parseISO(event.endsAt) : defaultDates.end,
    category:
      (event?.category?.id ? eventCategories.find(({ id }) => id === event.category.id)?.id : undefined) ??
      eventCategories[0].id,
    maxParticipants: event?.maxParticipants?.toString() || "",
    noParticipantsLimit: !event?.maxParticipants && event?.participantCount != null,
    allowGuests: event == null ? true : event.maxGuestsPerParticipant !== 0,
    image: event?.image ? [event.image] : [],
    type: event?.type ?? "common",
    participantCount: isClone ? 0 : (event?.participantCount ?? 0),
    audience: event?.audience || [],
    eventObserver: event?.projectConnection
      ? eventObservers.find((p) => p.id === event.projectConnection?.id)!
      : eventObservers[0],
    publishAt: !event?.publishedAt && event?.publishAt ? parseISO(event.publishAt) : undefined,
    recurrenceCount: "1",
  };
}

export function useAddEvents(): { isSubmitting: boolean; create: (data: CreateOrEditEventFormValues) => void } {
  const slug = useSlug();
  const navigate = useNavigate();
  const { uploadFormImage, isUploadingImage } = useUploadImage();

  const addEventsMutation = eventMutations.useAddEvents();

  const create = useCallback(
    async (formValues: CreateOrEditEventFormValues) => {
      if (!formValues.start) {
        throw new Error("Missing start date");
      }

      if (!formValues.end) {
        throw new Error("Missing end date");
      }

      const payload: CreateEventsRequest = {
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        startsAt: formValues.start.toISOString(),
        endsAt: formValues.end.toISOString(),
        location: formValues.location?.trim() || undefined,
        maxParticipants: getMaxParticipants(formValues.noParticipantsLimit, formValues.maxParticipants),
        maxGuestsPerParticipant: formValues.allowGuests ? undefined : 0,
        type: formValues.type,
        categoryType: formValues.category,
        audience: formValues.audience,
        projectConnectionId: formValues.eventObserver?.type === "project" ? undefined : formValues.eventObserver.id,
        publishAt: formValues.publishAt ? formValues.publishAt.toISOString() : undefined,
        recurrenceCount: parseInt(formValues.recurrenceCount),
        recurrence: formValues.recurrence || undefined,
      };

      if (formValues.image.length > 0) {
        const uploadedImage = await uploadFormImage(formValues.image[0]);

        payload.imageId = uploadedImage?.id;
      }

      addEventsMutation.mutate(payload, {
        onSuccess: (res) => {
          const createdEvents = res.events;

          // Navigate to the first occuring event
          void navigate(routes.events.details({ slug, id: createdEvents[0].id }));
        },
      });
    },
    [addEventsMutation, navigate, slug, uploadFormImage],
  );

  return {
    isSubmitting: addEventsMutation.isPending || isUploadingImage,
    create,
  };
}

export function useEditEvent(eventId: string): {
  isSubmitting: boolean;
  edit: (data: CreateOrEditEventFormValues) => void;
} {
  const slug = useSlug();
  const navigate = useNavigate();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const updateEventMutation = eventMutations.useUpdateEvent();

  const edit = useCallback(
    async (formValues: CreateOrEditEventFormValues) => {
      if (!formValues.start) {
        throw new Error("Missing start date");
      }

      if (!formValues.end) {
        throw new Error("Missing end date");
      }

      const data: UpdateEventRequest = {
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        startsAt: formValues.start.toISOString(),
        endsAt: formValues.end.toISOString(),
        location: formValues.location?.trim() || undefined,
        maxParticipants: getMaxParticipants(formValues.noParticipantsLimit, formValues.maxParticipants),
        maxGuestsPerParticipant: formValues.allowGuests ? undefined : 0,
        categoryType: formValues.category,
        audience: formValues.audience,
        publishAt: formValues.publishAt ? formValues.publishAt.toISOString() : undefined,
      };

      if (formValues.image.length > 0) {
        const image = await uploadFormImage(formValues.image[0]);

        data.imageId = image?.id;
      }

      updateEventMutation.mutate(
        {
          eventId,
          data,
        },
        {
          onSuccess: (editedEvent) => {
            void navigate(routes.events.details({ slug, id: editedEvent.id }));
          },
        },
      );
    },
    [updateEventMutation, eventId, uploadFormImage, navigate, slug],
  );

  return {
    isSubmitting: updateEventMutation.isPending || isUploadingImage,
    edit,
  };
}
